import { Divider, Stack, Typography } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import { useState } from 'react';

import ConfirmationDialog from '@/components/dialogs/confirmationDialog';
import OptionMenu from '@/components/option-menu';
import { Panels } from '@/components/panels/panels';
import type { ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';

export const BackupScopeDynamicResourcesViewerHeader = ({
  resourcesScopeType,
  condition,
  include,
  exclude,
}: {
  resourcesScopeType: ResourcesScopeType;
  condition?: Condition;
  include?: string[];
  exclude?: string[];
}) => {
  const [resetResourcesScopeOpen, setResetResourcesScopeOpen] = useState(false);
  const { rightPanel, snackbar } = useWorkspace();
  const caughtResourcesCount = 99;
  const allResourcesCount = 99;

  const onResetResourcesScopeConfirm = async () => {
    await save();
    setResetResourcesScopeOpen(false);
  };

  const save = async (
    condition?: Condition,
    include?: string[],
    exclude?: string[]
  ) => {
    // todo: call server to save
    throw new Error('Not implemented', {
      cause: { condition, include, exclude },
    });
  };

  const editResourceScope = () => {
    rightPanel.setComponent({
      panel: Panels.BackupScopeResourcesWizard,
      props: {
        condition,
        resourcesScopeType,
        include,
        exclude,
        onSave: async (condition, include, exclude) => {
          await save(condition, include, exclude);
          snackbar.showMessage('Resource scope updated');
        },
        onAbort: () => rightPanel.setIsOpen(false),
      },
    });
    rightPanel.setIsOpen(true, PanelSize.Full);
  };

  return (
    <>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
      >
        <Typography variant='h4'>Dynamic resource selection</Typography>
        <Stack direction='row' alignItems='center' gap={'24px'}>
          <Typography variant='subtitle1'>
            {caughtResourcesCount === allResourcesCount
              ? `All resources in scope (${allResourcesCount})`
              : `${caughtResourcesCount} of ${allResourcesCount} resources in scope`}
          </Typography>
          <Divider orientation='vertical' flexItem variant='middle' />
          <OptionMenu
            options={[
              {
                text: 'Edit resource scope',
                menuItemProps: {
                  onClick: editResourceScope,
                },
                testId: 'edit-dynamic-resources',
              },
              {
                text: 'Reset (includes all resources)',
                menuItemProps: {
                  onClick: () => setResetResourcesScopeOpen(true),
                  disabled: resourcesScopeType === 'allIncluded',
                },
                testId: 'reset-dynamic-resources',
              },
            ]}
          />
        </Stack>
      </Stack>
      <ConfirmationDialog
        isOpen={resetResourcesScopeOpen}
        onCancel={() => setResetResourcesScopeOpen(false)}
        onConfirm={onResetResourcesScopeConfirm}
        confirmSnackbarText='Resource scope reset'
        title='Reset resource scope'
        description='Are you sure you want to reset the resource scope?'
        buttonTitle='Reset'
      />
    </>
  );
};
